import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import classnames from 'classnames'

// Components
import { Player } from '../../../../Player'
import Text from '../../../../Text'
import Question from '../../../../Questions/QuestionSingleContainer'
import { Button, Page } from '../../../../..'

// Selectors
import {
  selectCustomerFirstName,
  selectCustomerPhotoUri,
} from '../../../../../../store/selectors/customerProfile'
import { selectClientFetchId } from '../../../../../../store/selectors/client'
import {
  selectHomeId,
  selectHomeAddressStateName,
} from '../../../../../../store/selectors/home'

// Context
import { useTheme } from '@homebot/shared-ui-swashbuckle'

// Utils
import { SYSTEM_REQUEST } from '../../../../../../constants/messages'
import { HOME_VALUE_TOPIC_KEY } from '../../../../../../constants/topicKeys'
import { PATH_HELPERS } from '../../../../../../constants/navigation'
import {
  NonDisclosureLearnMoreLoad,
  NonDisclosureTuneValue,
} from '../../../../../../constants/trackingEvents'
import { dispatchEvent } from '../../../../../../helpers/tagManager'
import history from '../../../../../../history'
import FiftyStatesSvg from './FiftyStatesSvg'
import LightFiftyStatesSvg from './LightFiftyStatesSvg'

// Styles
import styles from './NonDisclosureWarning.module.scss'

const message = defineMessages({
  placeholder: {
    id: 'Homeowner.nonDisclosureWarning.footer.questionPlaceHolder',
    defaultMessage: 'Can you verify my home value?',
  },
})

const NonDisclosureWarning = () => {
  const avatarUri = useSelector(selectCustomerPhotoUri)
  const clientId = useSelector(selectClientFetchId)
  const firstName = useSelector(selectCustomerFirstName)
  const homeId = useSelector(selectHomeId)
  const intl = useIntl()
  const stateName = useSelector(selectHomeAddressStateName)
  const { theme } = useTheme()

  const Svg = theme.isLightThemeType ? LightFiftyStatesSvg : FiftyStatesSvg

  useEffect(() => {
    dispatchEvent(NonDisclosureLearnMoreLoad)
  }, [])

  const handleNavigation = () => {
    history.push(PATH_HELPERS.tuneYourValue.buildPath(clientId, homeId))
  }

  return (
    <Page>
      <Page.Body data-qa='non-disclosure-warning-learn-more-panel'>
        <section className={classnames(styles.section, styles.header)}>
          <Text element='h4' variant='neutralCool' variantCount='900' size='l'>
            <FormattedMessage
              id='Homeowner.nonDisclosureState.title'
              defaultMessage='{stateName} limits public access to home sale information'
              values={{ stateName }}
            />
          </Text>
        </section>
        <section className={styles.section}>
          <Player videoType='wistia' videoId='1dfkdhj7bh' title='' />
        </section>
        <section className={styles.section} />
        <section className={styles.section}>
          <div className={styles.row}>
            <div className={styles.column}>
              <Text
                element='p'
                variant='neutralCool'
                variantCount='700'
                size='s'
              >
                <FormattedMessage
                  id='Homeowner.nonDisclosureState.body1'
                  defaultMessage='These states are called “non-disclosure” states, and they do not require the home’s sale price to be submitted to the county office.'
                />
              </Text>
              <div className={styles.divider} />
              <Text
                element='p'
                variant='neutralCool'
                variantCount='700'
                size='s'
              >
                <FormattedMessage
                  id='Homeowner.nonDisclosureState.body2'
                  defaultMessage='In areas with less sale data, it can sometimes be difficult for our computer models to create confident value estimates for your home.'
                />
              </Text>
            </div>
          </div>
        </section>
        <section className={styles.section}>
          <Text element='h4' variant='neutralCool' variantCount='900' size='l'>
            <FormattedMessage
              id='Homeowner.nonDisclosureState.footer'
              defaultMessage='Don’t worry - we can get your home value dialed in quickly'
            />
          </Text>
          <div className={styles.cta}>
            <Text element='p' variant='neutralCool' variantCount='600' size='s'>
              <FormattedMessage
                id='Homeowner.nonDisclosureWarning.footerCta'
                defaultMessage='You can {tuneYourValueLink}, or ask {firstName} to help:'
                values={{
                  firstName,
                  tuneYourValueLink: (
                    <Button
                      data-qa='tune-your-value-link'
                      minimal
                      element='a'
                      onClick={handleNavigation}
                      track={NonDisclosureTuneValue}
                    >
                      <FormattedMessage
                        id='Homeowner.nonDisclosureWarning.footerCtaTuneLink'
                        defaultMessage='tune your value'
                      />
                    </Button>
                  ),
                }}
              />
            </Text>
          </div>
          <div className={styles.footer}>
            <Question
              questionTopic={intl.formatMessage(
                SYSTEM_REQUEST.nonDisclosureWarningTopic
              )}
              questionTopicKey={HOME_VALUE_TOPIC_KEY}
              placeholder={intl.formatMessage(message.placeholder)}
              avatarUri={avatarUri}
            />
          </div>
        </section>
        <section className={styles.section}>
          <div className={styles.svgContainer}>
            <Text
              element='span'
              variant='neutralCool'
              variantCount='900'
              size='l'
            >
              <FormattedMessage
                id='Homeowner.nonDisclosureState.mapKey'
                defaultMessage='12 non-disclosure states'
              />
            </Text>
            <Svg />
          </div>
        </section>
      </Page.Body>
    </Page>
  )
}

export default NonDisclosureWarning
